import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loginUser } from './authThunks';

// Roles that come from backend
export type UserRole = 'super_user' | 'company_admin' | 'user';

export interface Company {
  id: string;
  name: string;
}

// Auth state stored in Redux
export interface AuthState {
  // ✅ ADDED: User ID field for device assignment tracking
  id: number | null;
  isLoggedIn: boolean;
  email: string | null;
  name: string | null;
  role: UserRole | null;
  companyId: string | null;
  companies: Company[];
  loginTime?: number;
  expiresAt?: number;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
}

// Enhanced session storage validation old code
// const getValidatedUserFromSession = (): AuthState | null => {
//   try {
//     const userFromSession = sessionStorage.getItem('user');
//     const accessToken = sessionStorage.getItem('access_token');
//     const refreshToken = sessionStorage.getItem('refresh_token');

//     console.log('🔄 authSlice: Checking session storage on refresh...');
//     console.log('🔍 User data exists:', !!userFromSession);
//     console.log('🔍 Access token exists:', !!accessToken);
//     console.log('🔍 Refresh token exists:', !!refreshToken);

//     // Check if we have the minimum required data
//     if (!userFromSession || !accessToken || !refreshToken) {
//       console.log('❌ Missing required auth data in sessionStorage');
//       return null;
//     }

//     const parsedUser: AuthState = JSON.parse(userFromSession);

//     // Basic validation
//     if (!parsedUser || typeof parsedUser !== 'object') {
//       console.log('❌ Invalid user data structure');
//       return null;
//     }

//     // Check expiration
//     if (parsedUser.expiresAt && Date.now() > parsedUser.expiresAt) {
//       console.log('❌ Session expired');
//       sessionStorage.removeItem('user');
//       sessionStorage.removeItem('access_token');
//       sessionStorage.removeItem('refresh_token');
//       return null;
//     }

//     // ✅ ENHANCED: Check for required fields but allow companyId to be null
//     if (!parsedUser.isLoggedIn || !parsedUser.email || !parsedUser.role) {
//       console.log('❌ Missing required user fields');
//       return null;
//     }

//     console.log('✅ Session storage validation passed');
//     return parsedUser;
//   } catch (error) {
//     console.error('Error validating session storage:', error);
//     // Clear corrupted data
//     sessionStorage.removeItem('user');
//     sessionStorage.removeItem('access_token');
//     sessionStorage.removeItem('refresh_token');
//     return null;
//   }
// };

// Enhanced session storage validation with remember me support
const getValidatedUserFromSession = (): AuthState | null => {
  // First check localStorage for remember me (using the 'auth' key that SignInForm uses)
  const localAuth = localStorage.getItem('auth');
  if (localAuth) {
    try {
      const parsed = JSON.parse(localAuth);

      // Convert the thunk response format to AuthState format
      const authState: AuthState = {
        id: parsed.user?.id || null,
        isLoggedIn: true,
        email: parsed.user?.email || null,
        name: parsed.user?.name || null,
        role: (parsed.user?.role_name as UserRole) || null,
        companyId: parsed.user?.companyId || null,
        companies:
          parsed.user?.companyId && parsed.user?.company_name
            ? [
                {
                  id: parsed.user.companyId,
                  name: parsed.user.company_name
                }
              ]
            : [],
        loginTime: Date.now(),
        expiresAt: Date.now() + 60 * 60 * 1000,
        access_token: parsed.tokens?.access_token,
        refresh_token: parsed.tokens?.refresh_token,
        token_type: parsed.tokens?.token_type
      };

      // Also store tokens in sessionStorage for the thunk/auth flow
      if (parsed.tokens?.access_token) {
        sessionStorage.setItem('access_token', parsed.tokens.access_token);
      }
      if (parsed.tokens?.refresh_token) {
        sessionStorage.setItem('refresh_token', parsed.tokens.refresh_token);
      }
      // Also store the complete user state in sessionStorage for consistency
      sessionStorage.setItem('user', JSON.stringify(authState));

      return authState;
    } catch {
      localStorage.removeItem('auth');
    }
  }

  // Check sessionStorage for 'auth' (when remember me is NOT checked)
  const sessionAuth = sessionStorage.getItem('auth');
  if (sessionAuth) {
    try {
      const parsed = JSON.parse(sessionAuth);

      // Convert the thunk response format to AuthState format
      const authState: AuthState = {
        id: parsed.user?.id || null,
        isLoggedIn: true,
        email: parsed.user?.email || null,
        name: parsed.user?.name || null,
        role: (parsed.user?.role_name as UserRole) || null,
        companyId: parsed.user?.companyId || null,
        companies:
          parsed.user?.companyId && parsed.user?.company_name
            ? [
                {
                  id: parsed.user.companyId,
                  name: parsed.user.company_name
                }
              ]
            : [],
        loginTime: Date.now(),
        expiresAt: Date.now() + 60 * 60 * 1000,
        access_token: parsed.tokens?.access_token,
        refresh_token: parsed.tokens?.refresh_token,
        token_type: parsed.tokens?.token_type
      };

      // Store tokens in sessionStorage
      if (parsed.tokens?.access_token) {
        sessionStorage.setItem('access_token', parsed.tokens.access_token);
      }
      if (parsed.tokens?.refresh_token) {
        sessionStorage.setItem('refresh_token', parsed.tokens.refresh_token);
      }
      sessionStorage.setItem('user', JSON.stringify(authState));

      return authState;
    } catch {
      sessionStorage.removeItem('auth');
    }
  }

  // Then check localStorage for 'user' key (your original check)
  const localUser = localStorage.getItem('user');
  if (localUser) {
    try {
      const parsed = JSON.parse(localUser);

      if (!parsed.expiresAt || Date.now() < parsed.expiresAt) {
        return parsed;
      } else {
        localStorage.removeItem('user');
      }
    } catch {
      localStorage.removeItem('user');
    }
  }

  try {
    const userFromSession = sessionStorage.getItem('user');
    const accessToken = sessionStorage.getItem('access_token');
    const refreshToken = sessionStorage.getItem('refresh_token');

    if (!userFromSession || !accessToken || !refreshToken) {
      return null;
    }

    const parsedUser: AuthState = JSON.parse(userFromSession);

    if (!parsedUser || typeof parsedUser !== 'object') {
      return null;
    }

    if (parsedUser.expiresAt && Date.now() > parsedUser.expiresAt) {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      return null;
    }

    if (!parsedUser.isLoggedIn || !parsedUser.email || !parsedUser.role) {
      return null;
    }

    return parsedUser;
  } catch (error) {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    return null;
  }
};

// Initialize state from session storage
const initializeState = (): AuthState => {
  const validatedUser = getValidatedUserFromSession();

  if (validatedUser) {
    return validatedUser;
  } else {
    return {
      // ✅ ADDED: Initialize id as null
      id: null,
      isLoggedIn: false,
      email: null,
      name: null,
      role: null,
      companyId: null,
      companies: []
    };
  }
};

const initialState: AuthState = initializeState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Login success action
    loginSuccess: (
      state,
      action: PayloadAction<Omit<AuthState, 'loginTime' | 'expiresAt'>>
    ) => {
      const secureUserData: AuthState = {
        ...action.payload,
        loginTime: Date.now(),
        expiresAt: Date.now() + 60 * 60 * 1000 // 1 hour
      };

      sessionStorage.setItem('user', JSON.stringify(secureUserData));

      return {
        ...state,
        ...secureUserData
      };
    },

    // Logout action
    logout: state => {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('auth');
      localStorage.removeItem('auth'); // Add this line
      localStorage.removeItem('user');
      return {
        // ✅ ADDED: Reset id to null on logout
        id: null,
        isLoggedIn: false,
        email: null,
        name: null,
        role: null,
        companyId: null,
        companies: []
      };
    },

    // Validate current session (used on app load)
    validateSession: state => {
      const currentUser = getValidatedUserFromSession();
      if (!currentUser) {
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        return {
          // ✅ ADDED: Reset id to null on session validation failure
          id: null,
          isLoggedIn: false,
          email: null,
          name: null,
          role: null,
          companyId: null,
          companies: []
        };
      }

      return { ...state, ...currentUser };
    },

    // Refresh tokens action
    refreshTokens: (
      state,
      action: PayloadAction<{ access_token: string; refresh_token?: string }>
    ) => {
      const { access_token, refresh_token } = action.payload;

      sessionStorage.setItem('access_token', access_token);
      if (refresh_token) {
        sessionStorage.setItem('refresh_token', refresh_token);
      }

      // Update state with new tokens
      state.access_token = access_token;
      if (refresh_token) {
        state.refresh_token = refresh_token;
      }
      state.expiresAt = Date.now() + 60 * 60 * 1000; // Reset expiry
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        const { user, tokens } = action.payload;

        // Create the complete auth state
        const authState: AuthState = {
          // ✅ ADDED: Store user ID from backend response
          id: user.id,
          isLoggedIn: true,
          email: user.email,
          name: user.name,
          role: user.role_name as UserRole,
          companyId: user.companyId,
          companies:
            user.companyId && user.company_name
              ? [
                  {
                    id: user.companyId,
                    name: user.company_name
                  }
                ]
              : [],
          loginTime: Date.now(),
          expiresAt: Date.now() + 60 * 60 * 1000,
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          token_type: tokens.token_type
        };

        // Store in sessionStorage for persistence
        sessionStorage.setItem('user', JSON.stringify(authState));
        sessionStorage.setItem('access_token', tokens.access_token);
        sessionStorage.setItem('refresh_token', tokens.refresh_token);

        return authState;
      })
      .addCase(loginUser.rejected, (state, action) => {
        // Clean up on failure
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');

        return {
          // ✅ ADDED: Reset id to null on login failure
          id: null,
          isLoggedIn: false,
          email: null,
          name: null,
          role: null,
          companyId: null,
          companies: []
        };
      });
  }
});

export const { loginSuccess, logout, validateSession, refreshTokens } =
  authSlice.actions;
export default authSlice.reducer;
